import { supabase } from '@/lib/supabase'

interface OnboardingMessage {
  title: string
  description: string
  onboarding_message_id: number
}

const ONBOARDING_MESSAGES: OnboardingMessage[] = [
  {
    onboarding_message_id: 1,
    title: 'Welcome to Framed — your project starts here',
    description: `This is your **project inbox** — the single place where everything about your website lands: feedback, monitoring alerts, suggestions, and tasks.

Right now it's filled with a few welcome messages to show you around. Once you start working, these will be replaced by real items from your team, your clients, and Framed's automated signals.

**Three things you can do right now:**

1. **Explore the sidebar** — Discovery, Review, Monitoring, and Settings are all one click away
2. **Press** \`Cmd+K\` — the command palette lets you jump anywhere instantly
3. **Read through these messages** — each one shows a different part of what Framed can do

When you're done, just mark these as complete (select an item and press \`E\`) and they'll disappear from your inbox. They won't come back.`,
  },
  {
    onboarding_message_id: 2,
    title: 'Build — go from idea to build-ready spec in one session',
    description: `Most website projects fail before a single line of code is written. Requirements live in scattered docs, Slack threads, and "I'll know it when I see it" conversations.

Framed's **Discovery** phase fixes this. Walk through a guided flow that captures everything a builder needs:

**What gets captured:**

- **Business context** — who you are, your audience, what success looks like
- **Page structure** — which pages you need, with smart presets for your industry
- **Visual direction** — colors, fonts, style keywords, reference sites
- **Functionality** — CMS, auth, integrations, technical requirements
- **Content** — per-page sections with wireframe layout selection

**What comes out:**

A complete **Build Spec** — a structured document you can export as a prompt for any AI tool or hand to a developer. Supported targets:

- **AI builders**: Bolt, Lovable, v0, Windsurf
- **AI coding**: Claude, Cursor
- **Traditional**: Markdown files, developer brief

One Discovery session replaces weeks of back-and-forth. Open **Discovery** in the sidebar to try it.`,
  },
  {
    onboarding_message_id: 3,
    title: 'Review — visual feedback without the email chains',
    description: `Collecting website feedback usually means annotated screenshots in email threads, vague descriptions like "the thing on the right looks off," and a spreadsheet to track it all.

Framed replaces that entire process with the **Visual Widget** — a feedback tool you drop onto any live or staging site.

**How it works:**

Your client opens the site, clicks the Framed button, and can:

- **Pin comments** directly on elements — click a button, image, or section and leave a note exactly where the issue is
- **Suggest text edits** — select any text on the page and propose a change. You see a clean diff of what they want changed
- **Annotate screenshots** — draw arrows, rectangles, circles, and add text labels on a full-page screenshot
- **Record their screen** — capture a video showing exactly what's happening, including scroll behavior and interactions
- **Attach files** — drag in reference images, mockups, or documents

Everything lands in this inbox as a structured ticket with context: which page, which element, a screenshot, and the exact feedback. No more "what did they mean by that?"

**No account needed for reviewers.** You create a session link, share it, and feedback flows in.

Press \`W\` to launch a widget session, or find it in the sidebar under **Review**.`,
  },
  {
    onboarding_message_id: 4,
    title: 'Manage & Monitor — your site on autopilot',
    description: `Once your site is live, Framed watches it for you. Automated **Signals** run on a schedule and create inbox items when something needs attention — just like the ones in the demo project.

**Here's what monitoring looks like in practice:**

---

**SSL & Domain Monitoring**
Framed tracks your SSL certificate expiry and domain registration. If auto-renewal fails or a certificate is about to expire, you get an item like this:

> *"SSL certificate expires in 12 days — action required"*
> Certificate details, impact analysis, auto-renewal error logs, and step-by-step fix instructions — all in one place.

---

**Performance Tracking**
Automated Lighthouse runs catch regressions before your users do:

> *"Performance regression: Mobile score dropped to 64 (was 89)"*
> Core Web Vitals comparison, root cause analysis (e.g., unoptimized hero image), and prioritized fix list with estimated time savings.

---

**SEO Audits**
Regular crawls surface broken links, missing meta tags, and indexing issues:

> *"SEO audit: 3 critical issues affecting search rankings"*
> Every issue mapped to a specific page with fix instructions and priority ranking.

---

**Uptime Monitoring**
Endpoint health checks with incident timelines and response time trends:

> *"Weekly uptime report: 99.82% — 2 incidents detected"*
> Full incident breakdown with root causes, affected endpoints, and weekly response time charts.

---

Each signal creates a real inbox item you can assign, comment on, estimate, and track to completion. Configure which signals to run in **Settings → Signals**.`,
  },
  {
    onboarding_message_id: 5,
    title: 'Tokens — pay for what you use, nothing more',
    description: `Framed uses a **token-based** model instead of monthly subscriptions. You buy tokens when you need them, and each operation costs a predictable amount.

**What costs tokens:**

- **AI generation** — build specs, improvement suggestions, meeting note summaries
- **Monitoring runs** — SEO audits, performance scans, uptime checks
- **Asset processing** — screenshot capture, website scraping

**What's free:**

- **Unlimited projects** — create as many as you need
- **Unlimited team members** — invite your whole team
- **Feedback collection** — the visual widget and all inbox features
- **Time tracking** — log hours, manage packages
- **All views and exports** — dashboards, changelogs, reports

**How it adds up:**

A typical monthly monitoring setup (weekly performance + SEO + uptime digest) costs roughly **15–20 tokens per month**. A full Discovery-to-Build-Spec generation is around **8–12 tokens**. Most agencies spend less on Framed in a year than one month of comparable tools.

Your account started with **free welcome tokens** — enough to explore every feature. Check your balance in the sidebar, or visit **Settings → Billing** to add more.`,
  },
  {
    onboarding_message_id: 6,
    title: 'Pro tips — work faster with keyboard shortcuts',
    description: `Framed is built for speed. Every action is one or two keystrokes away.

**Navigation**

- \`Cmd/Ctrl + K\` — **Command palette**. Search for anything: projects, sections, actions. The fastest way to move around.
- \`P\` — **Project switcher**. Jump between projects without going back to the dashboard.
- \`Escape\` — **Close** whatever's open: modals, drawers, dropdowns.

**Create**

- \`N\` — **New item**. Opens the new ticket form.
- \`S\` — **New suggestion**. Log an improvement idea.
- \`M\` — **New meeting note**. Capture notes from a call or meeting.

**Actions**

- \`E\` — **Complete or archive** the selected item, then auto-advance to the next one. Great for triaging your inbox.
- \`W\` — **Launch widget**. Start a visual feedback session on your live or staging site.

Single-key shortcuts only trigger when you're not typing in a text field, so they'll never interfere with writing comments or descriptions.

**One more thing:** try \`Cmd/Ctrl + K\` right now. It's the quickest way to see everything Framed can do.`,
  },
]

/**
 * Seeds onboarding inbox messages for a new project.
 * Idempotent — checks for existing onboarding items before inserting.
 */
export async function seedOnboardingItems(
  projectId: string,
  agencyId: string | null,
  userId?: string | null
): Promise<boolean> {
  // Check if onboarding items already exist
  const { data: existing, error: checkError } = await supabase
    .from('maintenance_items')
    .select('id')
    .eq('project_id', projectId)
    .eq('metadata->>onboarding', 'true')
    .limit(1)

  if (checkError) {
    console.error('[onboarding] Failed to check existing items:', checkError)
    return false
  }

  if (existing && existing.length > 0) {
    return false
  }

  // Build rows with staggered created_at (message 1 = oldest, message 6 = newest/top)
  const now = Date.now()
  const rows = ONBOARDING_MESSAGES.map((msg, index) => ({
    project_id: projectId,
    agency_id: agencyId,
    title: msg.title,
    description: msg.description,
    category: 'other' as const,
    priority: 'low' as const,
    status: 'backlog' as const,
    source: 'information' as const,
    created_by: userId || null,
    created_by_name: 'Framed',
    metadata: {
      onboarding: true,
      onboarding_message_id: msg.onboarding_message_id,
    },
    comments: [],
    attachments: [],
    created_at: new Date(now + index * 60_000).toISOString(),
  }))

  const { error: insertError } = await supabase
    .from('maintenance_items')
    .insert(rows)

  if (insertError) {
    console.error('[onboarding] Failed to seed items:', insertError)
    return false
  }

  return true
}
